import { useRef, useEffect } from 'react';

export function useLongPress(callback, ms = 500, threshold = 10) {
    const timerRef = useRef();
    const callbackRef = useRef(callback);
    const startX = useRef(0);
    const startY = useRef(0);
    const moved = useRef(false);
    const longPressed = useRef(false);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    const start = (e) => {
        longPressed.current = false;

        const touch = e.touches?.[0];

        if (touch) {
            startX.current = touch.clientX;
            startY.current = touch.clientY;
        }

        moved.current = false;

        // Don't prevent default or stop propagation immediately
        // Let other gesture handlers (like pull-to-refresh) have a chance
        timerRef.current = setTimeout(() => {
            if (!moved.current) {
                longPressed.current = true;
                callbackRef.current(e);
            }
        }, ms);
    };

    const move = (e) => {
        const touch = e.touches?.[0];
        if (!touch) return;

        const dx = Math.abs(touch.clientX - startX.current);
        const dy = Math.abs(touch.clientY - startY.current);

        // Check if this is a vertical swipe (potential pull-to-refresh)
        const isVerticalSwipe = dy > dx && dy > threshold;
        const isPullDown = (touch.clientY - startY.current) > threshold;

        if (dx > threshold || dy > threshold) {
            moved.current = true;
            clear();
            
            // If it's a pull-down gesture, don't interfere with it
            if (isVerticalSwipe && isPullDown) {
                // Let the pull-to-refresh handle this
                return;
            }
        }
    };

    const clear = () => {
        clearTimeout(timerRef.current);
        timerRef.current = null;
    };

    const handleClick = (e, onClickFallback) => {
        if (!longPressed.current && typeof onClickFallback === 'function') {
            onClickFallback(e);
        }
    };

    return {
        onTouchStart: start,
        onTouchMove: move,
        onTouchEnd: (e) => {
            clear();
            handleClick(e, callbackRef.current.onTap);
        },
        onTouchCancel: clear,
        onMouseDown: start,
        onMouseUp: (e) => {
            clear();
            handleClick(e, callbackRef.current.onTap);
        },
        onMouseLeave: clear,
    };
}
